import { useState, useCallback, useEffect } from 'react'
import { PlayIcon, PauseIcon, CopyIcon } from './icons'
import { useAudioPlayer, SPEED_OPTIONS } from './useAudioPlayer'
import './AudioPlayer.scss'
import AudioItem from './AudioPlayer.interface'
import Consts from '../../Interfaces/Consts'

/* --- Types -------------------------------------------------------------- */

/* --- Component ---------------------------------------------------------- */

export const Audio: React.FC<AudioItem> = ({
    audioUrl,
    title,
    subtitle,
    date,
    thumbnailUrl,
    icon,
    itemId,
    playerId = 'player',
    onPlay,
    isActive = true,
}) => {
    const { waveformRef, hoverRef, timeRef, isPlaying, duration, isReady, handlePlayPause, speed, handleSpeedSelect } =
        useAudioPlayer({ audioUrl, playerId, onPlay, isActive })

    const [copied, setCopied] = useState(false)

    const handleCopyLink = useCallback(async () => {
        const clearHref = window.location.origin + window.location.pathname;
        const url = clearHref + "#audio-" + itemId;
        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            /* clipboard unavailable — silently ignore */
        }
    }, [itemId])

    useEffect(() => {
        const hash = window.location.hash
        if (hash) {
            // Wait for all items to render
            const timeoutId = setTimeout(() => {
                const element = document.querySelector(hash)
                element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 200)

            return () => clearTimeout(timeoutId)
        }
    }, [/* dependency array - probably your audio items array */])

    return (
        <article className="ap" aria-label="Audio player" id={`audio-${itemId}`}>
            {/* -- Sidebar thumbnail -- */}
            {thumbnailUrl ? (
                <aside
                    className="ap__sidebar"
                    style={{ backgroundImage: `url(${thumbnailUrl})` }}
                    aria-label="Thumbnail"
                />
            ) : icon ? (
                <aside
                    className="ap__sidebar icon"
                    aria-label="Icon"
                >
                    <i className={icon + " fa-3x"}></i>
                </aside>
            ) : ""}

            {/* -- Main content -- */}
            <div className="ap__content">
                {/* Info row */}
                <header className="ap__info">
                    <div className="ap__info-header">
                        <div className="ap__meta">
                            <p className="ap__title" title={title}>{title}</p>
                            {subtitle && <span className="ap__subtitle">{subtitle}</span>}
                        </div>
                    </div>
                    {date && new Date(date) > new Date(0) && <span className="ap__date">{new Date(date).toLocaleDateString("he-IL")}</span>}
                </header>

                {/* Waveform container */}
                <div className="ap__wave-section" dir="rtl">
                    <div className="ap__wave-container">
                        <div className="ap__wave-row">
                            {/* Speed selector dropdown */}
                            <div className="ap__speed-dropdown dropdown">
                                <button
                                    type="button"
                                    className="ap__speed-btn"
                                    data-toggle="dropdown"
                                    aria-expanded="false"
                                    aria-label={`Playback speed: ${speed}x`}
                                >
                                    <span className="ap__speed-num">{speed}</span>x
                                </button>
                                <ul className="dropdown-menu ap__speed-menu">
                                    {SPEED_OPTIONS.map(s => (
                                        <li key={s}>
                                            <button
                                                type="button"
                                                className={`ap__speed-item${speed === s ? ' ap__speed-item--active' : ''}`}
                                                onClick={() => handleSpeedSelect(s)}
                                            >
                                                {s}x
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Waveform + hover overlay */}
                            <div ref={waveformRef} className="ap__waveform">
                                <div ref={timeRef} className="ap__wave-time" aria-live="polite" aria-label="Current time">0:00</div>
                                <div className="ap__wave-duration" aria-label="Duration">{duration}</div>
                                <div ref={hoverRef} className="ap__wave-hover" aria-hidden="true" />
                            </div>

                            {/* Play / Pause */}
                            <button
                                className={`ap__play-btn ${isPlaying ? 'pause' : 'play'}`}
                                onClick={handlePlayPause}
                                disabled={!isReady}
                                aria-label={isPlaying ? 'Pause' : 'Play'}
                            >
                                {isPlaying ? <PauseIcon /> : <PlayIcon />}
                            </button>


                        </div>

                    </div>
                </div>

                {/* Controls row */}
                <footer className="ap__controls">
                    <button
                        className={`ap__share-btn${copied ? ' ap__share-btn--copied' : ''}`}
                        onClick={handleCopyLink}
                        aria-label={Consts.copyLink}
                    >
                        <CopyIcon />
                        <span>{copied ? Consts.copied : Consts.copyLink}</span>
                    </button>
                </footer>
            </div>
        </article>
    )
}

export default Audio